import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import Close from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { GfgButton } from "@/components/ui";
import { useOrgAllowances, useUpdateOrganizationCreditSettings } from "@/hooks/useOrganizations";
import type {
  OrgAllowances,
  Organization,
  OrgCreditSettingsPatch,
  PaymentTermsChange,
} from "@/interfaces/organization";
import { humanErrorMessage } from "@/lib/entitlementError";
import { formatUsd } from "@/utils/number-format";

type Pane = "limit" | "postpaid" | "history";

const money = (v: string | number) => formatUsd(v);
const num = (v: string | number) => Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 });
const onOff = (v: boolean | null) => (v ? "On" : "Off");
const days = (n: number | null) => `${n ?? 0} day${n === 1 ? "" : "s"}`;
const lagText = (n: number | null) => (n ? `${days(n)} after close` : "on the close date");
/** The methods a postpaid client's terms may name, in ExpectedPaymentMethod order. */
const PAY_BY = [
  { value: 0, label: "ACH" },
  { value: 1, label: "Wire" },
  { value: 2, label: "Card" },
] as const;
const payBy = (m: number | null) => PAY_BY.find((p) => p.value === m)?.label ?? "—";
/** "23 Sep" — day then short month, as the rest of the admin reads. */
const shortDate = (iso: string) => {
  const d = new Date(iso);
  return `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })}`;
};
const stamp = (iso: string) =>
  `${shortDate(iso)}, ${new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;

/** What one proposal changes, as [setting, from, to] — the banner, history and dialog all read it. */
function changesOf(c: PaymentTermsChange): { setting: string; from: string; to: string }[] {
  const out: { setting: string; from: string; to: string }[] = [];
  if (c.billbackEligibleTo !== null) {
    out.push({
      setting: `Allow ${FUNDING_MODEL_NAME.postpaid}`,
      from: onOff(c.billbackEligibleFrom),
      to: onOff(c.billbackEligibleTo),
    });
  }
  if (c.creditLimitTo !== null) {
    out.push({
      setting: "Credit limit",
      from: num(c.creditLimitFrom ?? 0),
      to: num(c.creditLimitTo),
    });
  }
  if (c.overrideCreditLimitTo !== null) {
    out.push({
      setting: "Override limit",
      from: onOff(c.overrideCreditLimitFrom),
      to: onOff(c.overrideCreditLimitTo),
    });
  }
  if (c.minUpfrontPcTo !== null) {
    out.push({
      setting: "Minimum upfront",
      from: `${Number(c.minUpfrontPcFrom ?? 0)}%`,
      to: `${Number(c.minUpfrontPcTo)}%`,
    });
  }
  if (c.finalInvoiceLagDaysTo !== null) {
    out.push({
      setting: "Final invoice issued",
      from: lagText(c.finalInvoiceLagDaysFrom),
      to: lagText(c.finalInvoiceLagDaysTo),
    });
  }
  if (c.netTermsDaysTo !== null) {
    out.push({
      setting: "Payment terms",
      from: `Net ${c.netTermsDaysFrom ?? 0}`,
      to: `Net ${c.netTermsDaysTo}`,
    });
  }
  if (c.finalPaymentMethodTo !== null) {
    out.push({
      setting: "Pay by",
      from: payBy(c.finalPaymentMethodFrom),
      to: payBy(c.finalPaymentMethodTo),
    });
  }
  return out;
}

/**
 * GFG · Organization → Allowances (mockups 1 / 1b / 1c). The client's credit limit and postpaid
 * eligibility, both under maker-checker: saving RAISES A PROPOSAL, a second GFG approver applies it,
 * and while one is in flight nothing here can be edited. Every control shows the value IN FORCE,
 * never the proposed one.
 */
export default function AllowancesPanel({
  org,
  initialPane = "limit",
}: {
  org: Organization;
  /** Which pane opens first — a deep link, or a banner pointing at the pending change. */
  initialPane?: Pane;
}) {
  const { data, isLoading, error } = useOrgAllowances(org.id);

  if (isLoading) return <p className="font-lato text-[14px]">Loading…</p>;
  if (error || !data) {
    return (
      <p className="font-lato text-[14px] text-good-wine">
        {humanErrorMessage(error, "Couldn't load this organization's allowances.")}
      </p>
    );
  }

  return <AllowancesBody org={org} data={data} initialPane={initialPane} />;
}

/** One pending edit — what the strip shows as a chip, and what Save sends. */
interface DraftChange {
  key: string;
  pane: Pane;
  setting: string;
  from: string;
  to: string;
  /** Put the field back to the value in force; the chip goes with it. */
  revert: () => void;
}

/**
 * The edits on BOTH tabs, held together, so one Save raises one proposal carrying every change —
 * a credit limit and the postpaid terms can go to the approver as a single request. Each edit that
 * differs from the value in force is a `DraftChange`; setting it back removes it.
 */
function useAllowanceDraft(orgId: number, data: OrgAllowances) {
  const limit = Number(data.creditLimit);
  const upfrontInForce = Number(data.minUpfrontPc);
  const [display, setDisplay] = useState(num(limit));
  const [override, setOverride] = useState(data.overrideCreditLimit);
  const [allowed, setAllowed] = useState(data.billbackEligible);
  // The postpaid terms, as typed — strings so a field can be cleared while editing.
  const [upfrontInput, setUpfrontInput] = useState(String(upfrontInForce));
  const [lagInput, setLagInput] = useState(String(data.finalInvoiceLagDays));
  const [netInput, setNetInput] = useState(String(data.netTermsDays));
  const [method, setMethod] = useState(data.finalPaymentMethod);
  const propose = usePropose(orgId);

  const typed = Number(display.replace(/,/g, "").trim());
  const valid = display.trim() !== "" && Number.isFinite(typed) && typed >= 0;
  const upfront = Number(upfrontInput.trim());
  const upfrontValid =
    upfrontInput.trim() !== "" && Number.isFinite(upfront) && upfront >= 0 && upfront <= 100;
  const lag = Number(lagInput.trim());
  const net = Number(netInput.trim());
  const lagValid = lagInput.trim() !== "" && Number.isInteger(lag) && lag >= 0 && lag <= 90;
  const netValid = netInput.trim() !== "" && Number.isInteger(net) && net >= 1 && net <= 365;

  const changes: DraftChange[] = [];
  if (valid && Math.abs(typed - limit) >= 0.005) {
    changes.push({
      key: "creditLimit",
      pane: "limit",
      setting: "Credit limit",
      from: num(limit),
      to: num(typed),
      revert: () => setDisplay(num(limit)),
    });
  }
  if (override !== data.overrideCreditLimit) {
    changes.push({
      key: "overrideCreditLimit",
      pane: "limit",
      setting: "Override limit",
      from: onOff(data.overrideCreditLimit),
      to: onOff(override),
      revert: () => setOverride(data.overrideCreditLimit),
    });
  }
  if (allowed !== data.billbackEligible) {
    changes.push({
      key: "billbackEligible",
      pane: "postpaid",
      setting: `Allow ${FUNDING_MODEL_NAME.postpaid}`,
      from: onOff(data.billbackEligible),
      to: onOff(allowed),
      revert: () => setAllowed(data.billbackEligible),
    });
  }
  if (upfrontValid && Math.abs(upfront - upfrontInForce) >= 0.0001) {
    changes.push({
      key: "minUpfrontPc",
      pane: "postpaid",
      setting: "Minimum upfront",
      from: `${upfrontInForce}%`,
      to: `${upfront}%`,
      revert: () => setUpfrontInput(String(upfrontInForce)),
    });
  }
  if (lagValid && lag !== data.finalInvoiceLagDays) {
    changes.push({
      key: "finalInvoiceLagDays",
      pane: "postpaid",
      setting: "Final invoice issued",
      from: lagText(data.finalInvoiceLagDays),
      to: lagText(lag),
      revert: () => setLagInput(String(data.finalInvoiceLagDays)),
    });
  }
  if (netValid && net !== data.netTermsDays) {
    changes.push({
      key: "netTermsDays",
      pane: "postpaid",
      setting: "Payment terms",
      from: `Net ${data.netTermsDays}`,
      to: `Net ${net}`,
      revert: () => setNetInput(String(data.netTermsDays)),
    });
  }
  if (method !== data.finalPaymentMethod) {
    changes.push({
      key: "finalPaymentMethod",
      pane: "postpaid",
      setting: "Pay by",
      from: payBy(data.finalPaymentMethod),
      to: payBy(method),
      revert: () => setMethod(data.finalPaymentMethod),
    });
  }
  const changed = (key: string) => changes.some((c) => c.key === key);
  const allValid = valid && upfrontValid && lagValid && netValid;
  return {
    display,
    setDisplay,
    override,
    setOverride,
    allowed,
    setAllowed,
    typed,
    valid,
    upfrontInput,
    setUpfrontInput,
    upfrontValid,
    lagInput,
    setLagInput,
    lagValid,
    netInput,
    setNetInput,
    netValid,
    method,
    setMethod,
    changes,
    /**
     * Every field back to the value in force, on both panes — including one holding an invalid
     * entry, which has no chip. The strip empties with them.
     */
    revertAll: () => {
      setDisplay(num(limit));
      setOverride(data.overrideCreditLimit);
      setAllowed(data.billbackEligible);
      setUpfrontInput(String(upfrontInForce));
      setLagInput(String(data.finalInvoiceLagDays));
      setNetInput(String(data.netTermsDays));
      setMethod(data.finalPaymentMethod);
    },
    /** Any field that is not the value in force, valid or not — what Cancel has to undo. */
    edited:
      display !== num(limit) ||
      override !== data.overrideCreditLimit ||
      allowed !== data.billbackEligible ||
      upfrontInput !== String(upfrontInForce) ||
      lagInput !== String(data.finalInvoiceLagDays) ||
      netInput !== String(data.netTermsDays) ||
      method !== data.finalPaymentMethod,
    lines: changes.map((c) => `${c.setting} · ${c.from} → ${c.to}`),
    dirty: changes.length > 0 && allValid,
    propose,
    submit: (note: string) =>
      propose.submit({
        ...(changed("creditLimit") ? { creditLimit: typed } : {}),
        ...(changed("overrideCreditLimit") ? { overrideCreditLimit: override } : {}),
        ...(changed("billbackEligible") ? { billbackEligible: allowed } : {}),
        ...(changed("minUpfrontPc") ? { minUpfrontPc: upfront } : {}),
        ...(changed("finalInvoiceLagDays") ? { finalInvoiceLagDays: lag } : {}),
        ...(changed("netTermsDays") ? { netTermsDays: net } : {}),
        ...(changed("finalPaymentMethod") ? { finalPaymentMethod: method } : {}),
        note,
      }),
  };
}

type AllowanceDraft = ReturnType<typeof useAllowanceDraft>;

function AllowancesBody({
  org,
  data,
  initialPane,
}: {
  org: Organization;
  data: OrgAllowances;
  initialPane: Pane;
}) {
  const [pane, setPane] = useState<Pane>(initialPane);
  const draft = useAllowanceDraft(org.id, data);
  const pendingPostpaid =
    data.pending != null &&
    (data.pending.billbackEligibleTo != null ||
      data.pending.minUpfrontPcTo != null ||
      data.pending.netTermsDaysTo != null ||
      data.pending.finalInvoiceLagDaysTo != null ||
      data.pending.finalPaymentMethodTo != null);
  const pendingLimit =
    data.pending != null &&
    (data.pending.creditLimitTo != null || data.pending.overrideCreditLimitTo != null);
  const nav: { key: Pane; label: string; sub: string; dot: boolean }[] = [
    {
      key: "limit",
      label: "Credit limit",
      sub: data.overrideCreditLimit
        ? `${money(data.exposure.total)} · no limit applied`
        : `${money(data.exposure.total)} of ${money(data.creditLimit)} used`,
      dot: pendingLimit,
    },
    {
      key: "postpaid",
      label: FUNDING_MODEL_NAME.postpaid,
      sub: `${onOff(data.billbackEligible)}${pendingPostpaid ? " · change pending" : ""}`,
      dot: pendingPostpaid,
    },
    {
      key: "history",
      label: "Change history",
      sub: `${data.history.length} change${data.history.length === 1 ? "" : "s"}`,
      dot: false,
    },
  ];

  return (
    <div className="font-lato text-rodeo-black">
      {data.pending && <PendingBanner change={data.pending} />}
      {!data.pending && draft.changes.length > 0 && (
        <ChangeStrip changes={draft.changes} onOpen={setPane} />
      )}
      <div className="grid grid-cols-1 items-start gap-[26px] md:grid-cols-[232px_1fr]">
        <nav className="border-card-border md:sticky md:top-5 md:border-r">
          <div className="px-4 pb-2 text-[10px] font-bold uppercase tracking-[1.8px] text-graphite">
            Allowances
          </div>
          {nav.map((n) => (
            <button
              key={n.key}
              type="button"
              onClick={() => setPane(n.key)}
              aria-current={pane === n.key ? "page" : undefined}
              className={`flex w-full cursor-pointer items-center justify-between gap-2 border-l-[3px] py-3 pr-4 pl-[15px] text-left text-[14px] ${
                pane === n.key
                  ? "border-dark-dust bg-light-dust font-bold"
                  : "border-transparent hover:bg-clear-dust"
              }`}
            >
              <span>
                {n.label}
                <span className="mt-0.5 block text-[11px] font-normal text-graphite">{n.sub}</span>
              </span>
              {n.dot && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-good-ochre" />}
            </button>
          ))}
        </nav>
        <div className="min-w-0">
          {pane === "limit" && <CreditLimitPane org={org} data={data} draft={draft} />}
          {pane === "postpaid" && <PostpaidPane data={data} draft={draft} />}
          {pane === "history" && <HistoryPane history={data.history} />}
        </div>
      </div>
      <ProposeDialog
        open={draft.propose.open}
        lines={draft.lines}
        saving={draft.propose.saving}
        error={draft.propose.error}
        onCancel={draft.propose.cancel}
        onSubmit={draft.submit}
      />
    </div>
  );
}

/** The Save footer's line: what will go to the approver, across both tabs. */
function draftMessage(draft: AllowanceDraft): ReactNode {
  if (draft.propose.done) return "Sent for approval.";
  if (draft.changes.length > 1) {
    return `${draft.changes.length} changes — raised together as one approval`;
  }
  return null;
}

/**
 * Every unsaved edit, across both panes, as a chip — the blotter's filter-chip treatment. A chip
 * reflects the value as typed; setting it back to the value in force removes it, and × does that
 * for you. Clicking the chip opens the pane the setting lives on.
 */
function ChangeStrip({
  changes,
  onOpen,
}: {
  changes: DraftChange[];
  onOpen: (pane: Pane) => void;
}) {
  return (
    <div
      role="status"
      aria-label="Unsaved changes"
      className="mb-5 flex flex-wrap items-center gap-2 rounded-[10px] border border-card-border bg-clear-dust px-3.5 py-2.5"
    >
      <span className="mr-1 text-[11px] font-bold uppercase tracking-[1.4px] text-graphite">
        Unsaved changes
      </span>
      {changes.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-wax py-1 pr-2 pl-3 text-[13px]"
        >
          <button
            type="button"
            onClick={() => onOpen(c.pane)}
            className="cursor-pointer border-0 bg-transparent p-0 font-lato text-[13px] text-rodeo-black"
          >
            <strong className="font-bold">{c.setting}:</strong> {c.from} → {c.to}
          </button>
          <button
            type="button"
            aria-label={`Revert ${c.setting}`}
            onClick={c.revert}
            className="flex cursor-pointer border-0 bg-transparent p-0 text-graphite"
          >
            <Close sx={{ fontSize: 15 }} />
          </button>
        </span>
      ))}
    </div>
  );
}

function PendingBanner({ change }: { change: PaymentTermsChange }) {
  return (
    <div className="mb-5 rounded-[6px] border border-good-ochre-border bg-good-ochre-bg px-3.5 py-3 text-[13px] leading-[1.55] text-[#6b4816]">
      <b>Change awaiting approval.</b>{" "}
      {changesOf(change).map((c, i) => (
        <span key={c.setting}>
          {i > 0 && "; "}
          {c.setting} <b>{c.from}</b> → <b>{c.to}</b>
        </span>
      ))}
      , requested by {change.requestedByName ?? "a GFG user"} on {stamp(change.createdAt)}. Nothing
      here can be edited until a second GFG approver accepts or rejects it.{" "}
      <Link to="/actions" className="text-[#6b4816] underline">
        View in approvals →
      </Link>
    </div>
  );
}

function PaneHead({ title, lede }: { title: string; lede: ReactNode }) {
  return (
    <>
      <h2 className="mb-[3px] font-crimson text-[24px] font-normal leading-tight">{title}</h2>
      <p className="mb-[22px] max-w-[72ch] text-[12px] leading-[1.4] text-graphite">{lede}</p>
    </>
  );
}

/** A setting: what it is on the left, its control on the right (`.srow`). */
function SettingRow({
  title,
  caption,
  children,
}: {
  title: string;
  caption: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 border-card-border border-b py-[17px] first:pt-0 last:border-b-0 last:pb-0 md:flex-row md:gap-10">
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold">{title}</div>
        <div className="mt-[3px] max-w-[60ch] text-[12px] leading-[1.4] text-graphite">
          {caption}
        </div>
      </div>
      <div className="w-full shrink-0 md:w-[300px]">{children}</div>
    </div>
  );
}

/** The boxed checkbox control (`.ctl`), disabled when a change is in flight or not allowed. */
function CheckControl({
  checked,
  disabled,
  onChange,
  title,
  caption,
  pending,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  title: string;
  caption: string;
  pending?: boolean;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-[10px] border border-card-border px-4 py-3.5 ${
        disabled ? "cursor-default bg-wax text-graphite" : "cursor-pointer bg-white"
      }`}
    >
      <input
        type="checkbox"
        className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-dark-dust"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex-1">
        <span className="block text-[14px] font-semibold">
          {title}
          {pending && (
            <span className="ml-[7px] inline-block rounded-full border border-good-ochre-border bg-good-ochre-bg px-[11px] py-1 text-[11px] font-bold uppercase tracking-[1px] text-good-ochre">
              Pending
            </span>
          )}
        </span>
        <span className="mt-px block text-[12px] leading-[1.4] text-graphite">{caption}</span>
      </span>
    </label>
  );
}

/** The card a pane's settings sit in, with its Save footer (`.sec` + `.ft`). */
function SettingsCard({
  children,
  blocked,
  dirty,
  hasChanges,
  onSave,
  onCancel,
  message,
}: {
  children: ReactNode;
  blocked: boolean;
  dirty: boolean;
  /** Any unsaved edit, valid or not — what Cancel has to throw away. */
  hasChanges: boolean;
  onSave: () => void;
  onCancel: () => void;
  message: ReactNode;
}) {
  return (
    <div className="mb-[18px] overflow-hidden rounded-[10px] border border-card-border bg-white">
      <div className="px-5 pt-4 pb-5">{children}</div>
      <div className="flex items-center gap-3.5 border-card-border border-t bg-clear-dust px-5 py-[13px]">
        <GfgButton
          variant="accent"
          className="disabled:bg-smoke disabled:text-[#9a9a9a] disabled:opacity-100"
          disabled={blocked || !dirty}
          onClick={onSave}
        >
          Save — raises approval
        </GfgButton>
        <GfgButton variant="secondary" disabled={blocked || !hasChanges} onClick={onCancel}>
          Cancel
        </GfgButton>
        <span className="text-[12px] text-graphite">
          {blocked ? "Blocked while a change is pending" : message}
        </span>
      </div>
    </div>
  );
}

/**
 * Save opens this: the approver reads the note, so a proposal is never raised without one. What is
 * being proposed is restated so the maker confirms the delta, not the form.
 */
function ProposeDialog({
  open,
  lines,
  saving,
  error,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  lines: string[];
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth={false}
      slotProps={{
        paper: { sx: { borderRadius: "10px", width: 480, maxWidth: "calc(100vw - 32px)" } },
      }}
    >
      <div className="p-6 font-lato text-rodeo-black">
        <h3 className="m-0 font-crimson text-[22px] font-normal">Raise for approval</h3>
        <ul className="mt-2 mb-4 pl-5 text-[13px]">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
        <label
          htmlFor="allowances-proposal-note"
          className="mb-1.5 block text-[12px] font-bold uppercase tracking-[.4px] text-graphite"
        >
          Note for the approver
        </label>
        <textarea
          id="allowances-proposal-note"
          className="w-full rounded-[6px] border border-smoke px-3 py-2.5 text-[14px]"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why this change — the approver reads this"
        />
        {error && <p className="mt-2 mb-0 text-[13px] text-good-wine">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <GfgButton variant="secondary" disabled={saving} onClick={onCancel}>
            Cancel
          </GfgButton>
          <GfgButton
            variant="accent"
            disabled={saving || note.trim() === ""}
            onClick={() => onSubmit(note.trim())}
          >
            {saving ? "Raising…" : "Raise approval"}
          </GfgButton>
        </div>
      </div>
    </Dialog>
  );
}

/** The proposal flow: the dialog, the mutation, and what to say once it is raised. */
function usePropose(orgId: number) {
  const update = useUpdateOrganizationCreditSettings();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  return {
    open,
    error,
    done,
    saving: update.isPending,
    start: () => {
      setError(null);
      setDone(false);
      setOpen(true);
    },
    cancel: () => setOpen(false),
    submit: (patch: OrgCreditSettingsPatch) =>
      update.mutate(
        { id: orgId, patch },
        {
          onSuccess: () => {
            setOpen(false);
            setDone(true);
          },
          onError: (err) => setError(humanErrorMessage(err, "Couldn't raise the change.")),
        },
      ),
  };
}

function CreditLimitPane({
  org,
  data,
  draft,
}: {
  org: Organization;
  data: OrgAllowances;
  draft: AllowanceDraft;
}) {
  const blocked = data.pending !== null;
  const limit = Number(data.creditLimit);
  const exposure = Number(data.exposure.total);
  const provisional = data.exposure.items
    .filter((i) => i.kind === "provisional_lot")
    .reduce((s, i) => s + Number(i.credits), 0);
  const postpaid = exposure - provisional;
  const headroom = limit - exposure;
  const scale = Math.max(limit, exposure, 1);

  const { display, setDisplay, override, setOverride, typed, valid } = draft;
  const [showExposure, setShowExposure] = useState(false);

  return (
    <>
      <PaneHead
        title="Credit limit"
        lede={
          <>
            The most this client may owe us at any one time — credits provisioned before payment,{" "}
            <b>plus</b> the {FUNDING_MODEL_NAME.postpaid} portion of unsettled campaigns. One limit
            covers both.
          </>
        }
      />

      <div className="mb-[22px] flex overflow-hidden rounded-[10px] border border-card-border bg-clear-dust">
        <button
          type="button"
          onClick={() => setShowExposure(true)}
          className="group flex-1 cursor-pointer border-card-border border-r px-[18px] py-[15px] text-left transition-colors hover:bg-light-dust"
        >
          <div className="font-crimson text-[25px] leading-[1.15] tabular-nums">
            <span className="border-rodeo-dust border-b border-dotted group-hover:text-dust-ink">
              {money(exposure)}
            </span>
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[1.4px] text-graphite">
            Exposure today
          </div>
          <div className="mt-[5px] text-[10px] tracking-[.6px] text-rodeo-dust group-hover:text-dust-ink">
            ▸ Click for details
          </div>
        </button>
        <div className="flex-1 border-card-border border-r px-[18px] py-[15px]">
          <div
            className={`font-crimson text-[25px] leading-[1.15] tabular-nums ${
              headroom < 0 && !data.overrideCreditLimit ? "text-good-wine" : ""
            }`}
          >
            {data.overrideCreditLimit ? "—" : money(headroom)}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[1.4px] text-graphite">
            Remaining headroom
          </div>
        </div>
        <div className="flex-1 px-[18px] py-[15px]">
          <div className="font-crimson text-[25px] leading-[1.15] tabular-nums text-graphite">
            {data.overrideCreditLimit ? "No limit" : money(limit)}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-[1.4px] text-graphite">
            Credit limit
          </div>
        </div>
      </div>
      <div className="-mt-2.5 mb-[7px] flex h-[9px] overflow-hidden rounded-[5px] bg-wax">
        <i
          className="block h-full bg-good-night-3"
          style={{ width: `${(provisional / scale) * 100}%` }}
        />
        <i
          className="block h-full bg-dark-dust"
          style={{ width: `${(postpaid / scale) * 100}%` }}
        />
      </div>
      <div className="mb-[22px] flex flex-wrap gap-4 text-[12px] text-graphite">
        <span>
          <i className="mr-[5px] inline-block h-[9px] w-[9px] rounded-[2px] bg-good-night-3" />
          Provisioned, unpaid {money(provisional)}
        </span>
        <span>
          <i className="mr-[5px] inline-block h-[9px] w-[9px] rounded-[2px] bg-dark-dust" />
          {FUNDING_MODEL_NAME.postpaid} portions {money(postpaid)}
        </span>
        {!data.overrideCreditLimit && (
          <span>
            <i className="mr-[5px] inline-block h-[9px] w-[9px] rounded-[2px] border border-smoke bg-wax" />
            Headroom {money(Math.max(headroom, 0))}
          </span>
        )}
      </div>

      <SettingsCard
        blocked={blocked}
        dirty={draft.dirty}
        hasChanges={draft.edited}
        onSave={draft.propose.start}
        onCancel={draft.revertAll}
        message={draftMessage(draft)}
      >
        <SettingRow
          title="Credit limit"
          caption="Raising it is a two-person change: one GFG user proposes, another approves."
        >
          {blocked ? (
            <div className="flex items-center justify-between rounded-[6px] border border-smoke bg-wax px-3 py-2.5 text-[15px] text-graphite">
              <span>{num(limit)} USD</span>
              <span className="text-[12px]">locked</span>
            </div>
          ) : (
            <label className="flex items-center justify-between rounded-[6px] border border-smoke bg-white px-3 py-2.5 text-[15px]">
              <input
                type="text"
                inputMode="decimal"
                aria-label="Credit limit"
                className="w-full min-w-0 border-0 bg-transparent outline-none"
                value={display}
                disabled={override}
                onChange={(e) => setDisplay(e.target.value)}
                onBlur={() => valid && setDisplay(num(typed))}
              />
              <span className="ml-2 text-[12px] text-graphite">USD</span>
            </label>
          )}
          {!valid && (
            <p className="mt-1 mb-0 text-[12px] text-good-wine">Enter a non-negative number.</p>
          )}
        </SettingRow>
        <SettingRow
          title="Override the limit"
          caption={
            <>
              While on, the limit is not enforced at all — nothing blocks a provisional draw{" "}
              <b>or</b> the launch of a campaign on {FUNDING_MODEL_NAME.postpaid} terms.
            </>
          }
        >
          <CheckControl
            checked={override}
            disabled={blocked}
            onChange={setOverride}
            title={onOff(override)}
            caption={override ? "Limit is not enforced" : "Limit is enforced"}
          />
        </SettingRow>
      </SettingsCard>

      <ExposureDialog
        open={showExposure}
        onClose={() => setShowExposure(false)}
        orgName={org.name}
        data={data}
      />
    </>
  );
}

function ExposureDialog({
  open,
  onClose,
  orgName,
  data,
}: {
  open: boolean;
  onClose: () => void;
  orgName: string;
  data: OrgAllowances;
}) {
  const th =
    "border-smoke border-b px-2.5 pb-[9px] text-left text-[11px] font-bold uppercase tracking-[1.2px] text-graphite";
  const td = "border-card-border border-b px-2.5 py-3 align-middle";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: { sx: { borderRadius: "10px", width: 820, maxWidth: "calc(100vw - 32px)" } },
      }}
    >
      <div className="font-lato text-rodeo-black">
        <div className="flex items-start justify-between gap-5 border-card-border border-b px-[22px] pt-5 pb-3.5">
          <div>
            <h3 className="m-0 font-crimson text-[22px] font-normal">
              Exposure today — {money(data.exposure.total)}
            </h3>
            <div className="text-[12px] text-graphite">
              Everything counting against {orgName}'s credit limit right now.
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="cursor-pointer border-0 bg-transparent px-1.5 text-[22px] leading-none text-graphite hover:text-rodeo-black"
          >
            ×
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto px-[22px] pt-1.5 pb-5">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className={th}>Type</th>
                <th className={th}>Id no.</th>
                <th className={th}>Name</th>
                <th className={th}>Group</th>
                <th className={`${th} text-right`}>Value (credits)</th>
              </tr>
            </thead>
            <tbody>
              {data.exposure.items.map((i) => (
                <tr key={`${i.kind}-${i.id}`}>
                  <td className={td}>
                    {i.kind === "provisional_lot" ? (
                      <span className="rounded-[4px] bg-good-night-bg px-2 py-[3px] text-[10px] font-bold uppercase tracking-[1.2px] text-good-night">
                        Invoice
                      </span>
                    ) : (
                      <span className="rounded-[4px] bg-light-dust px-2 py-[3px] text-[10px] font-bold uppercase tracking-[1.2px] text-dust-ink">
                        Campaign
                      </span>
                    )}
                  </td>
                  <td className={`${td} tabular-nums`}>{i.id}</td>
                  <td className={`${td} ${i.kind === "postpaid_campaign" ? "" : "text-graphite"}`}>
                    {i.kind === "postpaid_campaign" ? (i.name ?? "—") : "—"}
                  </td>
                  <td className={td}>
                    Group {i.groupId} · {i.groupName}
                  </td>
                  <td className={`${td} text-right tabular-nums`}>{num(i.credits)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="border-rodeo-black border-t-2 px-2.5 py-3 font-bold" colSpan={4}>
                  Total exposure
                </td>
                <td className="border-rodeo-black border-t-2 px-2.5 py-3 text-right font-bold tabular-nums">
                  {num(data.exposure.total)}
                </td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-2.5 border-card-border border-t pt-2.5 text-[12px] leading-[1.5] text-graphite">
            <b>Invoice</b> rows are credits already issued to the client whose invoice is not yet
            paid. <b>Campaign</b> rows are the {FUNDING_MODEL_NAME.postpaid} portion of a campaign
            that has not settled — the amount we may still have to invoice after it closes. A
            campaign carries a name; an invoice does not.
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function PostpaidPane({ data, draft }: { data: OrgAllowances; draft: AllowanceDraft }) {
  const blocked = data.pending !== null;
  const requested = data.pending?.billbackEligibleTo ?? null;
  const { allowed, setAllowed } = draft;
  // Switching ON needs Enterprise; switching OFF is always allowed.
  const notOnPlan = !data.enterprise && !data.billbackEligible;

  return (
    <>
      <PaneHead
        title={FUNDING_MODEL_NAME.postpaid}
        lede={`Set up the client's ${FUNDING_MODEL_NAME.postpaid} terms and conditions.`}
      />
      <SettingsCard
        blocked={blocked}
        dirty={draft.dirty}
        hasChanges={draft.edited}
        onSave={draft.propose.start}
        onCancel={draft.revertAll}
        message={draftMessage(draft)}
      >
        <SettingRow
          title={`Allow ${FUNDING_MODEL_NAME.postpaid}`}
          caption="Requires Good Enterprise; below that the control is disabled and the option never renders for the client. The checkbox shows the value in force today, never the proposed one — if the approver rejects, nothing here changes."
        >
          <CheckControl
            checked={blocked ? data.billbackEligible : allowed}
            disabled={blocked || notOnPlan}
            onChange={setAllowed}
            title={onOff(blocked ? data.billbackEligible : allowed)}
            pending={requested !== null}
            caption={
              requested !== null
                ? `Requested: ${onOff(requested)}`
                : notOnPlan
                  ? "Disabled — not available on this plan"
                  : "A GFG admin may tick it; Save raises an approval"
            }
          />
        </SettingRow>
        <SettingRow
          title="Minimum upfront"
          caption={`The share of a campaign's budget paid as its deposit on ${FUNDING_MODEL_NAME.postpaid} terms, rounded up to whole gifts. 0 means no deposit; 100 means the whole budget is paid upfront and nothing is invoiced after close.`}
        >
          <DaysInput
            label="Minimum upfront"
            value={blocked ? String(Number(data.minUpfrontPc)) : draft.upfrontInput}
            onChange={draft.setUpfrontInput}
            locked={blocked}
            suffix="% of the budget"
            pending={data.pending?.minUpfrontPcTo != null}
            decimal
          />
          {!blocked && !draft.upfrontValid && (
            <p className="mt-1 mb-0 text-[12px] text-good-wine">
              Enter a percentage from 0 to 100.
            </p>
          )}
        </SettingRow>
        <SettingRow
          title="Final invoice issued"
          caption="Days after the campaign closes — the window in which redemptions are audited before the client is billed. 0 issues it on the close date."
        >
          <DaysInput
            label="Final invoice issued"
            value={blocked ? String(data.finalInvoiceLagDays) : draft.lagInput}
            onChange={draft.setLagInput}
            locked={blocked}
            suffix="days after close"
            pending={data.pending?.finalInvoiceLagDaysTo != null}
          />
          {!blocked && !draft.lagValid && (
            <p className="mt-1 mb-0 text-[12px] text-good-wine">Enter 0 to 90 whole days.</p>
          )}
        </SettingRow>
        <SettingRow
          title="Payment terms"
          caption="Days the client has to pay the final invoice, and a deposit invoice, from the day it is issued."
        >
          <DaysInput
            label="Payment terms"
            value={blocked ? String(data.netTermsDays) : draft.netInput}
            onChange={draft.setNetInput}
            locked={blocked}
            prefix="Net"
            suffix="days"
            pending={data.pending?.netTermsDaysTo != null}
          />
          {!blocked && !draft.netValid && (
            <p className="mt-1 mb-0 text-[12px] text-good-wine">Enter 1 to 365 whole days.</p>
          )}
        </SettingRow>
        <SettingRow
          title="Pay by"
          caption={`How the ${FUNDING_MODEL_NAME.postpaid} invoices are to be paid. Printed on the terms and set on each invoice.`}
        >
          {blocked ? (
            <div className="flex items-center justify-between rounded-[6px] border border-smoke bg-wax px-3 py-2.5 text-[15px] text-graphite">
              <span>{payBy(data.finalPaymentMethod)}</span>
              <span className="text-[12px]">locked</span>
            </div>
          ) : (
            <select
              aria-label="Pay by"
              className="w-full rounded-[6px] border border-smoke bg-white px-3 py-2.5 text-[15px]"
              value={draft.method}
              onChange={(e) => draft.setMethod(Number(e.target.value))}
            >
              {PAY_BY.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          )}
        </SettingRow>
      </SettingsCard>

      <div className="mt-5 mb-2.5 border-card-border border-b pb-[7px] text-[11px] font-bold uppercase tracking-[1.6px] text-graphite">
        Terms applied to campaigns on {FUNDING_MODEL_NAME.postpaid}
      </div>
      <dl className="m-0 grid max-w-[360px] grid-cols-[auto_1fr] gap-x-[18px] text-[14px]">
        <dt className="border-card-border border-b py-[7px] text-graphite">Minimum upfront</dt>
        <dd className="m-0 border-card-border border-b py-[7px] text-right font-semibold tabular-nums">
          {Number(data.minUpfrontPc)}%
        </dd>
        <dt className="border-card-border border-b py-[7px] text-graphite">Final invoice</dt>
        <dd className="m-0 border-card-border border-b py-[7px] text-right font-semibold tabular-nums">
          {lagText(data.finalInvoiceLagDays)}
        </dd>
        <dt className="border-card-border border-b py-[7px] text-graphite">Payment terms</dt>
        <dd className="m-0 border-card-border border-b py-[7px] text-right font-semibold tabular-nums">
          Net {data.netTermsDays} · {payBy(data.finalPaymentMethod)}
        </dd>
      </dl>
      <div className="mt-2.5 border-card-border border-t pt-2.5 text-[12px] leading-[1.5] text-graphite">
        Inherited by every campaign on {FUNDING_MODEL_NAME.postpaid} at launch and locked onto it,
        so a later change here never re-prices a campaign already running.
      </div>
    </>
  );
}

/** A whole-number (or, with `decimal`, percentage) field in the credit-limit input's style, or its locked read-out while pending. */
function DaysInput({
  label,
  value,
  onChange,
  locked,
  prefix,
  suffix,
  pending,
  decimal,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  locked: boolean;
  prefix?: string;
  suffix: string;
  pending?: boolean;
  /** Accept one decimal point — a percentage rather than whole days. */
  decimal?: boolean;
}) {
  if (locked) {
    return (
      <div className="flex items-center justify-between rounded-[6px] border border-smoke bg-wax px-3 py-2.5 text-[15px] text-graphite">
        <span>
          {prefix ? `${prefix} ` : ""}
          {value} {suffix}
        </span>
        <span className="text-[12px]">{pending ? "pending" : "locked"}</span>
      </div>
    );
  }
  return (
    <label className="flex items-center justify-between rounded-[6px] border border-smoke bg-white px-3 py-2.5 text-[15px]">
      {prefix && <span className="mr-2 text-[12px] text-graphite">{prefix}</span>}
      <input
        type="text"
        inputMode={decimal ? "decimal" : "numeric"}
        aria-label={label}
        className="w-full min-w-0 border-0 bg-transparent outline-none"
        value={value}
        onChange={(e) =>
          onChange(
            decimal
              ? e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
              : e.target.value.replace(/[^0-9]/g, ""),
          )
        }
      />
      <span className="ml-2 shrink-0 text-[12px] text-graphite">{suffix}</span>
    </label>
  );
}

function HistoryPane({ history }: { history: PaymentTermsChange[] }) {
  const th =
    "border-smoke border-b px-2.5 pb-[9px] text-left text-[11px] font-bold uppercase tracking-[1.2px] text-graphite";
  const td = "border-card-border border-b px-2.5 py-3 align-middle";
  return (
    <>
      <PaneHead
        title="Change history"
        lede={`Every change to the credit limit, ${FUNDING_MODEL_NAME.postpaid} and its invoicing terms, with who proposed it and who approved.`}
      />
      <div className="overflow-hidden rounded-[10px] border border-card-border bg-white">
        <div className="px-5 pt-4 pb-5">
          {history.length === 0 ? (
            <p className="m-0 text-[13px] text-graphite">No changes yet.</p>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th className={th}>Change</th>
                  <th className={th}>Requested by</th>
                  <th className={th}>Approved by</th>
                  <th className={th}>Note</th>
                  <th className={`${th} text-right`}>Date</th>
                </tr>
              </thead>
              <tbody>
                {history.flatMap((c) =>
                  changesOf(c).map((x, i) => (
                    <tr key={`${c.id}-${x.setting}`}>
                      <td className={td}>
                        {x.setting} · {x.from} → {x.to}
                      </td>
                      <td className={td}>{i === 0 ? (c.requestedByName ?? "—") : ""}</td>
                      <td className={`${td} ${c.status === "applied" ? "" : "text-graphite"}`}>
                        {i > 0
                          ? ""
                          : c.status === "pending"
                            ? "awaiting"
                            : c.status === "applied"
                              ? (c.decidedByName ?? "—")
                              : c.decidedByName
                                ? `rejected · ${c.decidedByName}`
                                : "withdrawn"}
                      </td>
                      <td className={`${td} text-graphite`}>{i === 0 ? c.note : ""}</td>
                      <td className={`${td} text-right`}>
                        {i === 0 ? shortDate(c.createdAt) : ""}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
